import { yupResolver } from "@hookform/resolvers/yup";
import { useBillingData } from "@sushicorp/contexts";
import { useFetchBillingData } from "@sushicorp/services";
import { usePutBillingData } from "@sushicorp/services";
import { useDeleteBillingData } from "@sushicorp/services";
import { DocumentType } from "artisn/types";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import Styles, { ModalHeaderStyled } from "./BillingDataPreview.styles";
import { DeleteBillingDataModal } from "./BillingDataPreview.styles";
import { BillingDataPreviewProps as Props } from "./BillingDataPreview.types";
import ListItem from "components/global/ListItem/ListItem";
import { ListItemActions } from "components/global/ListItem/ListItem.types";
import Modal from "components/global/Modal/Modal";
import BillingDataForm from "components/profileBilling/BillingDataForm/BillingDataForm";
import { getBillingDataFormSchema } from "components/profileBilling/BillingDataForm/BillingDataForm.helpers";
import useAuth from "contexts/auth/auth.context.hooks";
import useI18n from "hooks/useI18n";
import { notify } from "utils/common.utils";
import { dismissErrorNotification } from "utils/notifications.utils";
import { createErrorNotification } from "utils/notifications.utils";

import CloseSVG from "../../../../public/assets/images/close.svg";
import StudentSVG from "../../../../public/assets/images/student.svg";
import TrashSVG from "../../../../public/assets/images/trash.svg";

const BillingDataPreview: React.FC<Props> = props => {
  const t = useI18n();
  const { billingData, actions } = props;
  const [modalContent, setModalContent] = useState<
    ListItemActions | undefined
  >();
  const { name, document, default: isDefault, id } = billingData;
  const { additionalInfo } = billingData;
  const auth = useAuth();
  const putState = usePutBillingData(auth);
  const { mutate: updateBillingData, isLoading: isPutLoading } = putState;
  const { selectedBillingData, setSelectedBillingData } = useBillingData();
  const isActive = selectedBillingData?.id === id;
  const { mutate, isLoading: isDeleteLoading } = useDeleteBillingData(auth);
  const { data: billingDataList } = useFetchBillingData(auth, notify);
  const isLoading = isPutLoading || isDeleteLoading;
  const [documentType, setDocumentType] = useState<DocumentType>();
  const activeStyle = isActive ? "BillingDataList__item__radio--active" : "";

  const billingFormDefault = useMemo(() => {
    return {
      ...billingData,
      lastname: additionalInfo?.lastName,
      default: !billingDataList?.length ? true : billingData?.default,
      // @ts-ignore
      externalId: billingData.externalId
    };
  }, [billingData, billingDataList, additionalInfo]);

  const enhancedName = useMemo(() => {
    return `${name}`;
  }, [name]);

  const billingFormMethods = useForm({
    mode: "onChange",
    resolver: yupResolver(getBillingDataFormSchema(documentType)),
    defaultValues: billingFormDefault
  });

  const closeModalHandler = () => {
    setModalContent(undefined);
  };

  const onEdit = () => {
    setModalContent("edit");
  };

  const onDelete = () => {
    setModalContent("delete");
  };

  const onDefault = () => {
    updateBillingData(
      { ...billingData, default: true },
      {
        onError: () => {
          dismissErrorNotification();
          createErrorNotification(t.errors.updateBilling);
        }
      }
    );
  };

  const deleteHandler = () => {
    mutate(id, {
      onError: () => {
        dismissErrorNotification();
        createErrorNotification(t.errors.deleteBilling);
        console.error(
          "DeleteBillingDataModal: An error occurred while trying to delete billing data"
        );
      },
      onSuccess: () => {
        setSelectedBillingData(undefined);
        closeModalHandler();
      }
    });
  };

  useEffect(() => {
    if (!billingData || modalContent !== "edit") return;
    billingFormMethods.reset({
      ...billingData,
      lastname: additionalInfo?.lastName,
      // @ts-ignore
      externalId: billingData.externalId
    });
  }, [billingFormMethods, billingData, modalContent, additionalInfo]);

  const modalHeaderNode = (
    <ModalHeaderStyled>
      {modalContent === "edit" ? (
        <p className="ModalHeaderStyled__header">
          {t.billing.editBillingInformation}
        </p>
      ) : null}
    </ModalHeaderStyled>
  );

  return (
    <>
      <button
        className={`BillingDataList__item__radio ${activeStyle}`}
        onClick={() => onDefault()}
      />
      <Styles
        className="BillingDataPreview"
        isActive={isActive}
        disabled={isLoading}
      >
        <ListItem
          title={enhancedName}
          description={document}
          isDefault={isDefault}
          isActive={isActive}
          isLoading={isLoading}
          icon={<StudentSVG />}
          onEdit={onEdit}
          onDelete={onDelete}
          actions={actions}
        />
        {modalContent === "edit" ? (
          <Modal
            opened={!!modalContent}
            onClose={closeModalHandler}
            header={modalHeaderNode}
            closeIcon={<CloseSVG />}
          >
            {modalContent === "edit" ? (
              <FormProvider {...billingFormMethods}>
                <BillingDataForm
                  method="PUT"
                  billingData={billingData}
                  onClose={closeModalHandler}
                  onDocumentTypeChange={setDocumentType}
                  withBillingDataSync
                  hideLastName
                />
              </FormProvider>
            ) : null}
          </Modal>
        ) : null}
        <DeleteBillingDataModal
          opened={modalContent === "delete"}
          className="DeleteBillingDataModal"
          icon={<TrashSVG />}
          isTransparent
          title={t.billing.questionDeleteBillingInformation}
          description={t.billing.deleteBillingInformation}
          confirmText={t.common.delete}
          confirmAction={deleteHandler}
          cancelText={t.common.cancel}
          cancelAction={closeModalHandler}
          onClose={closeModalHandler}
        />
      </Styles>
    </>
  );
};

BillingDataPreview.defaultProps = {};

export default BillingDataPreview;
